import * as React from 'react';
import { toast } from 'react-toastify';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { getPriceText } from '../../utils/global/helper';
import { resendCheckoutLink } from '../../services/appointments';
import useStateCooldown from '../../hooks/useStateCooldown';
import useFeatureGuard from '../../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../../types/subscriptions';
import useSubscriptionModal from '../../hooks/subscriptions/useSubscriptionModal';
import { isAppointmentDepositPaid } from '../../helpers/appointments';
import useUser from '../../hooks/global/useUser';
import { Appointment, LoadedUser } from '../../interfaces';
import useGetAppointmentPayments from '../../hooks/appointments/useGetAppointmentPayments';
import {
  getMetadataFromPayment,
  getPaymentLineItems,
  isPaymentComplete,
} from '../../helpers/payments';
import PaymentLineItem from './PaymentLineItem';
import { cancelCheckout } from '../../services/payments';
import useCheckPendingPayment from '../../hooks/payments/useCheckPendingPayment';
import useInSplitCheckout from '../../hooks/payments/useInSplitCheckout';

type Props = {
  action: () => void;
  refreshModal: (appointmentId: string) => void;
  appointment: Appointment;
};

export default function PaymentSection({
  action,
  refreshModal,
  appointment,
}: Props) {
  const { user } = useUser<LoadedUser>();
  const terminalReaderId = user?.business?.terminalReaderId;

  const [resendDisabled, setResendDisabled] = useStateCooldown(15_000);
  const [loading, setLoading] = React.useState(false);

  const { openSubscriptionModal } = useSubscriptionModal();
  const canCheckout = useFeatureGuard(Feature.CHECKOUT);
  const isCheckoutDisabled = !appointment || appointment.status === 'CANCELED';
  const isInSplitCheckout = useInSplitCheckout();

  const { payments = [], isLoading: isLoadingPayments } =
    useGetAppointmentPayments(appointment);
  useCheckPendingPayment({
    appointment,
    appointmentPayments: payments,
    enabled: !!appointment && !isInSplitCheckout,
  });

  const depositPayment = payments?.find(
    payment => payment.type === 'DEPOSIT' && payment.status === 'SUCCESS',
  );
  const fullPayment = payments?.find(
    payment => payment.type === 'FULL' && payment.status === 'SUCCESS',
  );
  const depositPaymentMetadata = depositPayment
    ? getMetadataFromPayment(depositPayment)
    : null;
  const fullPaymentMetadata = fullPayment
    ? getMetadataFromPayment(fullPayment)
    : null;

  const serviceTaxRate = user?.business?.businessTaxRates?.serviceTaxRate || 0;
  const isDepositPaid = isAppointmentDepositPaid(appointment);

  const isFixedWithPrice =
    !!appointment?.price && appointment.priceType === 'FIXED';

  const getTotalDue = React.useCallback(
    (depositPaidOverride?: boolean) => {
      if (!appointment || appointment?.checkoutSessionCompleted) return 0;
      let tempTotalDue = isFixedWithPrice ? appointment.price! : 0;
      const tempIsDepositPaid = depositPaidOverride ?? isDepositPaid;
      if (tempIsDepositPaid) {
        tempTotalDue -= appointment.depositPrice!;
      }
      return Math.max(0, tempTotalDue);
    },
    [appointment, isDepositPaid, isFixedWithPrice],
  );

  const taxesPaid = React.useMemo(() => {
    const depositTaxes = (depositPaymentMetadata?.sales_tax || 0) / 100;
    const fullTaxes = (fullPaymentMetadata?.sales_tax || 0) / 100;
    return depositTaxes + fullTaxes;
  }, [depositPaymentMetadata?.sales_tax, fullPaymentMetadata?.sales_tax]);

  const totalTaxesDue = React.useMemo(
    () =>
      // Do not include deposit amount in taxes due if deposit was paid in cash
      getTotalDue(isDepositPaid && depositPayment?.method === 'CASH') *
      serviceTaxRate,
    [getTotalDue, isDepositPaid, depositPayment?.method, serviceTaxRate],
  );

  const taxesDue = React.useMemo(() => {
    if (!serviceTaxRate) return 0;

    const due = getTotalDue() * serviceTaxRate;

    return due;
  }, [serviceTaxRate, getTotalDue]);

  const totalDue = React.useMemo(() => {
    const tempTotalDue = getTotalDue();
    if (!tempTotalDue) return 0;
    return tempTotalDue + taxesDue;
  }, [getTotalDue, taxesDue]);

  const cancelAppointmentCheckout = async () => {
    if (!appointment || loading) return;
    try {
      setLoading(true);

      const terminalId =
        appointment.paymentMethod === 'TERMINAL' ? terminalReaderId : undefined;

      const response = await cancelCheckout({
        appointmentId: appointment.appointmentId,
        terminalReaderId: terminalId,
      });

      if (response) {
        const successMsg =
          appointment.paymentMethod === 'TERMINAL'
            ? 'Terminal checkout canceled.'
            : 'Checkout canceled.';
        toast.success(successMsg);
        refreshModal(appointment.appointmentId);
      }
    } catch (cancelError) {
      const errorMsg =
        appointment.paymentMethod === 'TERMINAL'
          ? 'Error canceling terminal checkout'
          : 'Error canceling checkout.';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const resendPaymentLink = async () => {
    if (loading || !canCheckout || !appointment || isCheckoutDisabled) return;
    try {
      if (
        !appointment.checkoutSessionActive ||
        appointment.checkoutSessionCompleted ||
        appointment.paymentMethod !== 'CREDIT'
      ) {
        return;
      }
      setLoading(true);
      const response = await resendCheckoutLink(appointment.appointmentId);
      if (response) {
        toast.success('Checkout link re-sent.');
        setResendDisabled(true);
      }
    } catch (resendError) {
      toast.error('Error re-sending checkout link');
    } finally {
      setLoading(false);
    }
  };

  const onCheckout = () => {
    if (isCheckoutDisabled) return;
    if (canCheckout) {
      action();
    } else {
      openSubscriptionModal();
    }
  };

  if (!appointment) return null;

  return (
    <>
      {!appointment.checkoutSessionActive &&
        !appointment.checkoutSessionCompleted &&
        !appointment.wasNoShow && (
          <div className="payment-section-table">
            <PaymentLineItem
              label="Total Price"
              value={
                isFixedWithPrice ? appointment.price || 0 : 'Add in checkout'
              }
            />

            {!!totalTaxesDue && (
              <PaymentLineItem
                label="Taxes"
                value={totalTaxesDue}
                subtract={false}
              />
            )}

            {isDepositPaid && (
              <PaymentLineItem
                label="Deposit Paid"
                value={appointment.depositPrice || 0}
                subtract
              />
            )}

            {!!taxesPaid && (
              <PaymentLineItem label="Taxes Paid" value={taxesPaid} subtract />
            )}

            <PaymentLineItem label="Total Due" value={totalDue || 0} isTotal />

            {!isFixedWithPrice && (
              <PaymentLineItem
                label="Quote"
                value={getPriceText(
                  appointment.priceType!,
                  appointment.minPrice,
                  appointment.maxPrice,
                  appointment.hourlyPrice,
                  appointment.price,
                )}
              />
            )}
            <Button
              variant="contained"
              onClick={onCheckout}
              className="primary"
              style={{ width: '100%' }}
              disabled={isCheckoutDisabled}>
              Checkout
            </Button>
          </div>
        )}

      {!!appointment.checkoutSessionCompleted && (
        <div className="payment-section-table">
          {isLoadingPayments ? (
            <CircularProgress size={24} />
          ) : (
            getPaymentLineItems({
              appointment,
              order:
                payments.filter(isPaymentComplete)?.find(p => p.order)?.order ||
                null,
              payments,
            }).map(({ amount, label, isTotal }) => (
              <PaymentLineItem
                key={label}
                label={label}
                value={Math.abs(amount)}
                isTotal={isTotal}
                subtract={amount < 0 || isTotal}
              />
            ))
          )}
        </div>
      )}

      {appointment.checkoutSessionActive &&
        !appointment.checkoutSessionCompleted &&
        appointment.paymentMethod === 'CREDIT' && (
          <div
            className="payment-section-table"
            style={{
              gap: '20px',
            }}>
            <Typography variant="body1" align="center">
              Checkout link sent. Waiting on payment.
            </Typography>
            <CircularProgress />
            {canCheckout && (
              <Box
                display="flex"
                justifyContent="space-around"
                alignItems="center"
                gap={2}
                width="100%">
                <Button
                  variant="text"
                  className="text-btn"
                  onClick={resendPaymentLink}
                  disabled={loading || resendDisabled}>
                  Resend
                </Button>
                <Button
                  variant="text"
                  className="text-btn"
                  onClick={cancelAppointmentCheckout}
                  disabled={loading}>
                  Cancel
                </Button>
              </Box>
            )}
          </div>
        )}

      {appointment.checkoutSessionActive &&
        !appointment.checkoutSessionCompleted &&
        appointment.paymentMethod === 'TERMINAL' && (
          <div
            className="payment-section-table"
            style={{
              gap: '20px',
            }}>
            <Typography variant="body1" align="center">
              Charge sent to payment terminal. Waiting on payment.
            </Typography>
            <Button
              variant="text"
              className="text-btn"
              onClick={cancelAppointmentCheckout}
              disabled={loading}>
              Having issues? Cancel charge on terminal
            </Button>
          </div>
        )}

      {!!appointment.wasNoShow && (
        <div
          className="payment-section-table"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            flexDirection: 'column',
            gap: '20px',
          }}>
          <Typography variant="body2" align="left">
            Customer marked as no-show
          </Typography>
        </div>
      )}
    </>
  );
}
